import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  CalendarDaysIcon, BookOpenIcon, AcademicCapIcon,
  CheckCircleIcon, XCircleIcon, ClipboardDocumentCheckIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .td * { box-sizing:border-box; }
  .td { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tdFade .4s ease both; }
  @keyframes tdFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  .td-welcome { margin-bottom:1.5rem; }
  .td-welcome-name { font-size:1.4rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .td-welcome-sub  { font-size:.78rem; color:#9094a8; margin-top:4px; }

  /* Check-in card */
  .checkin-card { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1.2rem 1.4rem; margin-bottom:1.4rem; display:flex; align-items:center; justify-content:space-between; gap:1rem; flex-wrap:wrap; }
  .checkin-info  { display:flex; align-items:center; gap:12px; flex:1; min-width:0; }
  .checkin-dot   { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
  .checkin-dot.in  { background:#10c98e; box-shadow:0 0 0 3px rgba(16,201,142,.2); animation:pulse 2s infinite; }
  .checkin-dot.out { background:#f59e0b; box-shadow:0 0 0 3px rgba(245,158,11,.2); }
  .checkin-dot.off { background:#d1d5db; }
  @keyframes pulse { 0%,100%{box-shadow:0 0 0 3px rgba(16,201,142,.2)} 50%{box-shadow:0 0 0 6px rgba(16,201,142,.05)} }
  .checkin-label { font-size:.78rem; font-weight:600; color:#1a1d2e; }
  .checkin-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .checkin-times { display:flex; gap:1.5rem; flex-shrink:0; flex-wrap:wrap; }
  .checkin-time-item  { text-align:center; }
  .checkin-time-label { font-size:.65rem; color:#9094a8; font-weight:600; text-transform:uppercase; letter-spacing:.05em; }
  .checkin-time-val   { font-size:.9rem; font-weight:700; font-family:'JetBrains Mono',monospace; color:#1a1d2e; margin-top:2px; }

  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 18px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-green { background:#10c98e; color:white; }
  .btn-green:hover { background:#0db87e; box-shadow:0 4px 14px rgba(16,201,142,.35); transform:translateY(-1px); }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover   { background:#ef4444; color:white; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }

  /* Stats */
  .stat-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:.9rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.1rem; border:1px solid #e8ebf4; }
  .s-icon { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; margin-bottom:.6rem; }
  .s-icon svg { width:15px; height:15px; stroke-width:1.8; }
  .s-icon.green{background:#ecfdf5;color:#10c98e} .s-icon.blue{background:#eef2ff;color:#3b6ef8}
  .s-icon.amber{background:#fffbeb;color:#f59e0b} .s-icon.red{background:#fef2f2;color:#ef4444}
  .s-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .s-value { font-size:1.5rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; line-height:1.1; }
  .s-sub   { font-size:.68rem; color:#b0b5cc; margin-top:3px; }

  /* Classes & subjects chip row */
  .chips-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:1rem 1.2rem; margin-bottom:1rem; }
  .chips-title   { font-size:.86rem; font-weight:600; color:#1a1d2e; margin-bottom:10px; }
  .class-chip    { display:inline-flex; align-items:center; gap:6px; padding:6px 12px; background:#eef2ff; color:#3b6ef8; border-radius:99px; font-size:.75rem; font-weight:600; margin:3px; }
  .subject-chip  { display:inline-flex; padding:4px 10px; background:#f3f0ff; color:#8b5cf6; border-radius:99px; font-size:.72rem; font-weight:600; margin:3px; }

  /* Two col */
  .two-col { display:grid; grid-template-columns:1fr 1fr; gap:1rem; margin-bottom:1rem; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .panel-head  { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .panel-sub   { font-size:.72rem; color:#9094a8; margin-top:1px; }
  .panel-link  { font-size:.74rem; color:#3b6ef8; cursor:pointer; font-weight:600; white-space:nowrap; }
  .panel-link:hover { text-decoration:underline; }

  .list-item { display:flex; align-items:flex-start; gap:10px; padding:11px 18px; border-bottom:1px solid #f5f6fb; }
  .list-item:last-child { border-bottom:none; }
  .list-item:hover { background:#fafbff; }
  .list-dot  { width:8px; height:8px; border-radius:50%; flex-shrink:0; margin-top:5px; }
  .list-dot.green{background:#10c98e} .list-dot.amber{background:#f59e0b} .list-dot.blue{background:#3b6ef8}
  .list-title { font-size:.8rem; font-weight:600; color:#1a1d2e; }
  .list-sub   { font-size:.7rem; color:#9094a8; margin-top:2px; }

  /* Attendance progress */
  .att-month { padding:14px 18px; }
  .att-month-row { display:flex; justify-content:space-between; align-items:center; margin-bottom:6px; }
  .att-month-label { font-size:.78rem; font-weight:600; color:#1a1d2e; }
  .att-month-pct   { font-family:'JetBrains Mono',monospace; font-size:.82rem; font-weight:700; }
  .progress-bar { height:8px; background:#f0f2f7; border-radius:99px; overflow:hidden; margin-bottom:10px; }
  .progress-fill { height:100%; border-radius:99px; transition:width .6s ease; }
  .att-stats { display:flex; gap:10px; flex-wrap:wrap; }
  .att-stat  { flex:1; min-width:60px; background:#f8f9fc; border-radius:9px; padding:8px 10px; text-align:center; }
  .att-stat-val   { font-family:'JetBrains Mono',monospace; font-size:1rem; font-weight:700; }
  .att-stat-label { font-size:.62rem; color:#9094a8; font-weight:600; text-transform:uppercase; margin-top:2px; }

  /* Class attendance table */
  .class-att-table { width:100%; border-collapse:collapse; }
  .class-att-table th { padding:8px 16px; text-align:center !important; font-size:.62rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; background:#fafbff; border-bottom:1px solid #edf0f9; white-space:nowrap; }
  .class-att-table td { padding:9px 16px; font-size:.78rem; border-bottom:1px solid #f3f5fb; vertical-align:middle; text-align:center !important; }
  .class-att-table tr:last-child td { border-bottom:none; }
  .cat-cards { display:none; padding:10px 18px; }
  .cat-card  { background:#f8f9fc; border:1px solid #e8ebf4; border-radius:12px; padding:12px; margin-bottom:8px; }
  .cat-row  { display:flex; justify-content:space-between; align-items:center; padding:3px 0; gap:10px; }
  .cat-row:not(:last-child) { border-bottom:1px solid #eceff8; }
  .cat-label { font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; white-space:nowrap; flex-shrink:0; }

  .td-loading { display:flex; align-items:center; justify-content:center; height:300px; gap:10px; color:#9094a8; font-size:.88rem; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#10c98e; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  .custom-tooltip { background:white; border:1px solid #e8ebf4; border-radius:10px; padding:8px 12px; font-family:'Sora',sans-serif; font-size:.74rem; box-shadow:0 4px 16px rgba(0,0,0,.1); }

  @media (max-width:900px) {
    .stat-grid { grid-template-columns:repeat(2,1fr); }
    .two-col   { grid-template-columns:1fr; }
  }
  @media (max-width:768px) {
    .td-welcome-name { font-size:1.2rem; }
    .checkin-card { flex-direction:column; align-items:flex-start; gap:.8rem; }
    .btn { width:100%; justify-content:center; }
    .cat-table-wrap { display:none !important; }
    .cat-cards { display:block; }
  }
`;

const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', hour12:true }) : '—';
const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { day:'numeric', month:'short' });

// e.g. "May, 2026"
const fmtMonthYear = () => new Date().toLocaleDateString('en-US', { month:'long', year:'numeric' });

// e.g. "02-May-2026"
const fmtTodayFull = () => {
  const d = new Date();
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleDateString('en-US', { month:'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
};

const TeacherDashboard = () => {
  const [data,      setData]      = useState(null);
  const [today,     setToday]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [actioning, setActioning] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchAll(); }, []);

  // Keep today's student-attendance summary current without a full page reload
  useEffect(() => {
    const interval = setInterval(refreshSummary, 20000);
    return () => clearInterval(interval);
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [dash, att] = await Promise.all([
        api.get('/teacher/dashboard'),
        api.get('/teacher/attendance/today'),
      ]);
      setData(dash.data);
      setToday(att.data);
    } catch { toast.error('Failed to load dashboard'); }
    finally  { setLoading(false); }
  };

  const refreshSummary = async () => {
    try { const { data: dash } = await api.get('/teacher/dashboard'); setData(dash); } catch { /* silent */ }
  };

  const handleCheckIn = async () => {
    try { setActioning(true); await api.post('/teacher/attendance/checkin'); toast.success('Checked in!'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.message || 'Check-in failed'); }
    finally { setActioning(false); }
  };
  const handleCheckOut = async () => {
    try { setActioning(true); await api.post('/teacher/attendance/checkout'); toast.success('Checked out!'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.message || 'Check-out failed'); }
    finally { setActioning(false); }
  };

  if (loading) return <div className="td"><style>{style}</style><div className="td-loading"><div className="spinner"/>Loading…</div></div>;

  const { teacher, monthSummary, upcomingExams, recentNotices, materialCount, classAttendanceSummary } = data || {};
  const classTeacherOf = teacher?.classTeacherOf;
  const rec    = today?.record;
  const hasIn  = !!rec?.checkIn;
  const hasOut = !!rec?.checkOut;

  const attPct = monthSummary?.workingDays
    ? Math.round(((monthSummary.presentDays || 0) / monthSummary.workingDays) * 100)
    : monthSummary?.presentDays ? Math.min(100, Math.round((monthSummary.presentDays / 22) * 100)) : 0;

  const chartData = classAttendanceSummary?.map(c => ({
    name: c.section ? `${c.className} — Sec ${c.section}` : c.className,
    present: c.presentToday || 0,
    absent:  c.absentToday  || 0,
    total:   c.totalStudents || 0,
    marked:  c.marked || false,
    isClassTeacher: c.isClassTeacher || false,
  })) || [];

  return (
    <div className="td"><style>{style}</style>

      <div className="td-welcome">
        <div className="td-welcome-name">Good morning, {teacher?.name?.split(' ')[0]} 👋</div>
      </div>

      {/* Check-in Card */}
      <div className="checkin-card">
        <div className="checkin-info">
          <div className={`checkin-dot ${hasOut?'out':hasIn?'in':'off'}`}/>
          <div>
            <div className="checkin-label">{hasOut?'Checked Out':hasIn?'Currently Checked In':'Not Checked In'}</div>
            <div className="checkin-sub">School: {today?.schoolStartTime||'09:00'} – {today?.schoolEndTime||'17:00'}{rec?.lateMinutes>0?` · ${rec.lateMinutes}m late`:''}</div>
          </div>
        </div>
        {(hasIn||hasOut)&&(
          <div className="checkin-times">
            <div className="checkin-time-item"><div className="checkin-time-label">Check In</div><div className="checkin-time-val">{fmtTime(rec?.checkIn)}</div></div>
            {hasOut&&<div className="checkin-time-item"><div className="checkin-time-label">Check Out</div><div className="checkin-time-val">{fmtTime(rec?.checkOut)}</div></div>}
            {hasOut&&rec?.workHours&&<div className="checkin-time-item"><div className="checkin-time-label">Hours</div><div className="checkin-time-val">{Number(rec.workHours||0).toFixed(1)}h</div></div>}
          </div>
        )}
        <div style={{width:'100%',maxWidth:200}}>
          {!hasIn&&<button className="btn btn-green" onClick={handleCheckIn} disabled={actioning}><CheckCircleIcon/>{actioning?'Please wait…':'Check In'}</button>}
          {hasIn&&!hasOut&&<button className="btn btn-red" onClick={handleCheckOut} disabled={actioning}><XCircleIcon/>{actioning?'Please wait…':'Check Out'}</button>}
          {hasOut&&<button className="btn btn-ghost" disabled>Done for today</button>}
        </div>
      </div>

      {/* Stats */}
      <div className="stat-grid">
        <div className="stat-card"><div className="s-icon green"><CalendarDaysIcon/></div><div className="s-label">Present This Month</div><div className="s-value">{monthSummary?.presentDays??0}</div><div className="s-sub">{monthSummary?.absentDays??0} absent · {monthSummary?.leaveDays??0} on leave</div></div>
        <div className="stat-card"><div className="s-icon blue"><AcademicCapIcon/></div><div className="s-label">Upcoming Exams</div><div className="s-value">{upcomingExams?.length??0}</div><div className="s-sub">in your classes</div></div>
        <div className="stat-card"><div className="s-icon amber"><ClipboardDocumentCheckIcon/></div><div className="s-label">Pending Leaves</div><div className="s-value">{monthSummary?.pendingLeaves??0}</div><div className="s-sub">awaiting approval</div></div>
        <div className="stat-card"><div className="s-icon green"><BookOpenIcon/></div><div className="s-label">Materials Uploaded</div><div className="s-value">{materialCount??0}</div><div className="s-sub">total resources</div></div>
      </div>

      {/* Classes & Subjects */}
      <div className="chips-panel">
        <div className="chips-title">My Classes & Subjects</div>
        <div style={{display:'flex',flexWrap:'wrap'}}>
          {teacher?.assignedClasses?.map(cls => {
            const secs = cls.sections?.length > 0 ? cls.sections : (cls.section ? [cls.section] : [null]);
            return secs.map(sec => (
              <span key={`${cls._id}-${sec||'all'}`} className="class-chip">
                {cls.name}{sec ? ` — Sec ${sec}` : ''}
              </span>
            ));
          })}
        </div>
        <div style={{marginTop:6,display:'flex',flexWrap:'wrap'}}>
          {teacher?.subjects?.map(s=><span key={s} className="subject-chip">{s}</span>)}
        </div>
        {classTeacherOf&&(
          <div style={{marginTop:10,display:'flex',alignItems:'center',gap:8,background:'#ecfdf5',border:'1px solid #a7f3d0',borderRadius:10,padding:'8px 12px',fontSize:'.78rem',fontWeight:600,color:'#065f46'}}>
            ★ You are the Class Teacher of {classTeacherOf.className}{classTeacherOf.section?` — Section ${classTeacherOf.section}`:''}
          </div>
        )}
      </div>

      {/* Attendance progress + class summary */}
      <div className="two-col">
        <div className="panel">
          <div className="panel-head">
            <div>
              {/* ── CHANGED: title now shows month + year ── */}
              <div className="panel-title">My Attendance — {fmtMonthYear()}</div>
            </div>
            <span className="panel-link" onClick={()=>navigate('/teacher/attendance')}>Full record</span>
          </div>
          <div className="att-month">
            <div className="att-month-row">
              <span className="att-month-label">Attendance Rate</span>
              <span className="att-month-pct" style={{color:attPct>=80?'#10c98e':attPct>=60?'#f59e0b':'#ef4444'}}>{attPct}%</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{width:`${attPct}%`,background:attPct>=80?'#10c98e':attPct>=60?'#f59e0b':'#ef4444'}}/>
            </div>
            <div className="att-stats">
              <div className="att-stat"><div className="att-stat-val" style={{color:'#10c98e'}}>{monthSummary?.presentDays||0}</div><div className="att-stat-label">Present</div></div>
              <div className="att-stat"><div className="att-stat-val" style={{color:'#ef4444'}}>{monthSummary?.absentDays||0}</div><div className="att-stat-label">Absent</div></div>
              <div className="att-stat"><div className="att-stat-val" style={{color:'#3b6ef8'}}>{monthSummary?.leaveDays||0}</div><div className="att-stat-label">On Leave</div></div>
              {monthSummary?.totalLateMinutes>0&&<div className="att-stat"><div className="att-stat-val" style={{color:'#f59e0b'}}>{monthSummary.totalLateMinutes}m</div><div className="att-stat-label">Late</div></div>}
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <div className="panel-title">Upcoming Exams</div>
            <span className="panel-link" onClick={()=>navigate('/teacher/datesheets')}>View all</span>
          </div>
          {upcomingExams?.length===0 ? (
            <div style={{padding:'2rem',textAlign:'center',color:'#9094a8',fontSize:'.8rem'}}>No upcoming exams</div>
          ) : upcomingExams?.map(e=>{
            const daysLeft=Math.ceil((new Date(e.date)-new Date())/86400000);
            return(
              <div key={e._id} className="list-item">
                <div className="list-dot green"/>
                <div style={{flex:1}}>
                  <div className="list-title">{e.title||e.subject}</div>
                  <div className="list-sub">{e.class?.name} · {fmtDate(e.date)}</div>
                </div>
                <div style={{fontSize:'.68rem',fontWeight:700,padding:'2px 8px',borderRadius:99,background:daysLeft<=3?'#fef2f2':daysLeft<=7?'#fffbeb':'#ecfdf5',color:daysLeft<=3?'#ef4444':daysLeft<=7?'#f59e0b':'#10c98e',flexShrink:0}}>
                  {daysLeft===0?'Today':daysLeft===1?'Tomorrow':`${daysLeft}d`}
                </div>
              </div>
            );
          })}

          <div style={{borderTop:'1px solid #f0f2f8'}}>
            <div className="panel-head" style={{borderBottom:'none',paddingBottom:6}}>
              <div className="panel-title">Recent Notices</div>
              <span className="panel-link" onClick={()=>navigate('/teacher/notices')}>View all</span>
            </div>
            {recentNotices?.length===0 ? (
              <div style={{padding:'1rem 18px',fontSize:'.78rem',color:'#9094a8'}}>No notices</div>
            ) : recentNotices?.slice(0,3).map(n=>(
              <div key={n._id} className="list-item">
                <div className="list-dot amber"/>
                <div><div className="list-title">{n.title}</div><div className="list-sub">{n.postedBy?.name} · {fmtDate(n.createdAt)}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Today's Student Attendance — separate from teacher's own attendance card */}
      <div className="panel">
        <div className="panel-head">
          <div>
            <div className="panel-title">Today's Student Attendance</div>
            <div className="panel-sub">Updates automatically as attendance is marked</div>
          </div>
          <span style={{fontSize:'.7rem',color:'#9094a8',fontFamily:"'JetBrains Mono',monospace"}}>{fmtTodayFull()}</span>
        </div>
        {chartData.length===0 ? (
          <div style={{padding:'2rem',textAlign:'center',color:'#9094a8',fontSize:'.8rem'}}>You are not the class teacher of any section yet</div>
        ) : (
          <>
            <div className="cat-table-wrap" style={{overflowX:'auto'}}>
              <table className="class-att-table">
                <thead><tr><th>Class</th><th>Present</th><th>Absent</th><th>%</th><th>Status</th></tr></thead>
                <tbody>
                  {chartData.map((c,i)=>{
                    const pct=c.total?Math.round((c.present/c.total)*100):0;
                    return(
                      <tr key={i}>
                        <td style={{fontWeight:600,color:'#0f1123'}}>{c.name}{c.isClassTeacher&&<span style={{marginLeft:6,fontSize:'.62rem',fontWeight:700,color:'#10c98e'}}>★ Class Teacher</span>}</td>
                        <td style={{color:'#10c98e',fontWeight:600,fontFamily:"'JetBrains Mono',monospace"}}>{c.present}</td>
                        <td style={{color:'#ef4444',fontFamily:"'JetBrains Mono',monospace"}}>{c.absent}</td>
                        <td><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.74rem',fontWeight:700,color:pct>=80?'#10c98e':pct>=60?'#f59e0b':'#ef4444'}}>{pct}%</span></td>
                        <td>{c.marked?<span style={{fontSize:'.68rem',fontWeight:700,color:'#10c98e'}}>✓ Marked</span>:<span style={{fontSize:'.68rem',fontWeight:700,color:'#9ca3af'}}>Not marked</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="cat-cards">
              {chartData.map((c,i)=>{
                const pct=c.total?Math.round((c.present/c.total)*100):0;
                return(
                  <div key={i} className="cat-card">
                    <div className="cat-row">
                      <span className="cat-label">Class</span>
                      <span style={{fontWeight:600,color:'#0f1123',fontSize:'.8rem'}}>{c.name}{c.isClassTeacher&&<span style={{marginLeft:6,fontSize:'.62rem',fontWeight:700,color:'#10c98e'}}>★</span>}</span>
                    </div>
                    <div className="cat-row">
                      <span className="cat-label">Present</span>
                      <span style={{color:'#10c98e',fontWeight:600,fontFamily:"'JetBrains Mono',monospace"}}>{c.present}</span>
                    </div>
                    <div className="cat-row">
                      <span className="cat-label">Absent</span>
                      <span style={{color:'#ef4444',fontFamily:"'JetBrains Mono',monospace"}}>{c.absent}</span>
                    </div>
                    <div className="cat-row">
                      <span className="cat-label">%</span>
                      <span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.74rem',fontWeight:700,color:pct>=80?'#10c98e':pct>=60?'#f59e0b':'#ef4444'}}>{pct}%</span>
                    </div>
                    <div className="cat-row">
                      <span className="cat-label">Status</span>
                      {c.marked?<span style={{fontSize:'.68rem',fontWeight:700,color:'#10c98e'}}>✓ Marked</span>:<span style={{fontSize:'.68rem',fontWeight:700,color:'#9ca3af'}}>Not marked</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TeacherDashboard;